import { Component, OnInit, Input, Output, EventEmitter, TemplateRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap, repeat } from 'rxjs/operators';
import { ReturnMessage } from 'src/app/models/return-message';
import { ToastControllerWrapperService } from 'src/app/services/toast-controller-wrapper/toast-controller-wrapper.service';
import { UserDataService } from 'src/app/services/user-data/user-data.service';
import { environment } from 'src/environments/environment';
import { AutocompleteDataLoader } from './autocomplete-data-loader';
import { AutocompleteOption } from './autocomplete-option';
import { FormatService } from 'src/app/services/format/format.service';
import { PhoneNumberFormat } from 'src/app/models/customer/phone-number-format';

/**
  <!-- Basic example with two way bind to a property -->
  <app-autocomplete label="User" [(selection)]="userSelection" [dataLoader]="loadUsers"></app-autocomplete>

  <!-- Basic example with using a form property -->
  <app-autocomplete label="User" formControlName="user" [dataLoader]="loadUsers"></app-autocomplete>

  <!-- Bind the label property to a variable -->
  <app-autocomplete [label]="userFieldLabel" [(selection)]="userSelection" [dataLoader]="loadUsers"></app-autocomplete>

  <!-- Custom 'No results' label -->
  <app-autocomplete label="User" labelNoResults="Nothing was found" [(selection)]="userSelection" [dataLoader]="loadUsers"></app-autocomplete>

  <!-- Event listener for the selectionChange event -->
  <app-autocomplete label="User" [(selection)]="userSelection" (selectionChange)="onUserSelected($event)" [dataLoader]="loadUsers"></app-autocomplete>

  <!-- Custom results list template -->
  <app-autocomplete label="User" [(selection)]="userSelection" [dataLoader]="loadUsers" [resultsTemplate]="users"></app-autocomplete>
  <ng-template #users let-options let-on="on">
    <ion-list *ngIf="options">
      <ion-item button *ngFor="let option of options" (click)="on.selectOption(option)">
        <ion-label>
          ID: {{option.id}} - Description: {{option.description}}
        </ion-label>
      </ion-item>
    </ion-list>
  </ng-template>

  <!-- Format function for the input field value after selection -->
  <app-autocomplete label="User" [formatValue]="formatUser" [(selection)]="userSelection" [dataLoader]="loadUsers" ></app-autocomplete>

  <!-- Custom option template -->
  <app-autocomplete label="User" [optionTemplate]="customOptionTemplate" [(selection)]="userSelection" [dataLoader]="loadUsers" ></app-autocomplete>
  <ng-template #customOptionTemplate let-option>
    <ion-card>
      <ion-card-header>
        <ion-card-subtitle>ID: {{option.Id}}</ion-card-subtitle>
        <ion-card-title>{{option.description}}</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        Some more information about the option selected...
      </ion-card-content>
    </ion-card>
  </ng-template>

 */
@Component({
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: AutocompleteComponent
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: AutocompleteComponent
    }
  ]
})
export class AutocompleteComponent implements OnInit, ControlValueAccessor, Validator {

  @Input() readonly: boolean = false;

  // The label value displayed on top of the field.
  @Input('label') labelValue: string;

  @Input('maxlength') maxlength: string;
  
  @Input('autocapitalize') autocapitalize: boolean = false;
  
  @Input('spellcheck') spellcheck: boolean = false;
  
  @Input('autocorrect') autocorrect: string = "off";

  // The label displayed when there are not results found after a search.
  @Input('labelNoResults') labelNoResultsValue: string;

  // Two way binding properties for the selected option
  @Input() selection: AutocompleteOption = null;
  @Output() selectionChange = new EventEmitter<AutocompleteOption>();


  updateOption(option: AutocompleteOption) {
    // Avoid any action when the option passed as psarameter is null or undefined and at the same time the existing selected option is null or undefined.
    if ((option === undefined || option === null || (typeof option === 'string' && (<string>option) === '')) &&
      (this.selection === undefined || this.selection === null)) {
      return;
    }

    // After setting the value of the input field set the skipSearch flag to true to avoid triggering an unwanted search IF the value of the autocomplete search term would change.
    if (this.getSearchTerm() != this.determineSearchTerm(option))
    {
      this.skipSearch = true;
    }
    
    // Set the option selected
    this.selection = option;

    // Do not emit the selectionChange event if updateOption is called before ngOnInit finished execution
    // because the default subscriber will not be there yet, instead call the setSearchTerm function directly 
    // to update the value displayed.
    if (this.initDone)
    {
      this.selectionChange.emit(this.selection);
    }
    else 
    {
      this.setSearchTerm(option);
    }
  }

  // The object used to load information.
  loader: AutocompleteDataLoader;

  // Setter function for the loader object
  @Input() set dataLoader(loader: AutocompleteDataLoader) {

    function instanceOfAutocompleteDataLoader(object: any): object is AutocompleteDataLoader {
      return 'load' in object && 'loadAll' in object;
    }
    if (instanceOfAutocompleteDataLoader(loader)) {
      this.loader = loader;
    }
    else {
      throw Error('loader object is not an instance of AutocompleteDataLoader');
    }

    this.results$ = this.searchTerms.pipe(
      // Add debounce to the field
      debounceTime(this.autocompleteDebounceTime),

      // switch to new search observable each time the term changes
      switchMap((term: string) => {
        let obs: Observable<ReturnMessage<AutocompleteOption[]>>;
        if (term !== '') {

          let searchValue = this.searchTerm;
          if (this.phoneNumberMode)
          {
            searchValue = this.formatService.unapplyPhoneNumberFormat(this.phoneNumberFormatObj.phoneNumberFormat, searchValue);
            searchValue = searchValue.replace(/0+$/, ''); // remove trailing zeros
            console.log('phone number search value', searchValue);
          }

          obs = this.loader.load(searchValue)
            .pipe(
              finalize(() => {
                this.loading = false;
              })
            );
        }
        else {
          obs = this.loader.loadAll()
            .pipe(
              finalize(() => {
                this.loading = false;
                this.allRequested = !this.allRequested;
              })
            );
        }
        return obs;
      }
      ),
      catchError(err => { return of(err.error) }),
      repeat()
    );

    this.results$.subscribe((response) => {
      if (response.success) {
        this.options = response.payload;
      }
      else {
        this.options = <AutocompleteOption[]>[];
      }
    }
    );
  }

  // Fuction used to format the value displayed after an option is selected
  // @Input() formatValue: (args: AutocompleteOption) => string;
  // Union Type: it can be a function or an object containing a function
  @Input() formatValue: ((args: AutocompleteOption) => string) | ({ config: any, format: (args: AutocompleteOption) => string });

  // Template for the results list
  @Input() resultsTemplate: TemplateRef<any>;

  // Template for the options in the list
  @Input() optionTemplate: TemplateRef<any>;

  // Option list
  options: AutocompleteOption[];

  // Value of the search input field
  searchTerm: string;

  getSearchTerm() { return this.searchTerm; }

  // Indicates if the user clicked the magnifying glass icon to load all the options
  allRequested: boolean = false;

  // Indicates if the control should skip the next search
  skipSearch: boolean = false;

  // Indicates if the control never executed a search
  initialSearch: boolean = true;

  // Used to indicate if the progress bar under the field shuld be visible or not
  loading: boolean = false;

  cntxt; // Used to access the selectOption function when specifying a template for the results list

  @Input() required: boolean = false;

  phoneNumberFormatObj: PhoneNumberFormat;
  phonePlaceholder: string;
  phoneNumberMask: (string | RegExp)[];

  @Input() phoneNumberMode: boolean = false;
  @Input() set phoneNumberFormat(aPhoneNumberFormat: PhoneNumberFormat)
  {
    if (aPhoneNumberFormat)
    {
      this.phoneNumberFormatObj = aPhoneNumberFormat;
      this.phonePlaceholder = this.formatService.createPhonePlaceHolder(this.phoneNumberFormatObj);
      this.phoneNumberMask = this.formatService.createPhoneNumberMask(this.phoneNumberFormatObj);
    }
  }

  constructor(private toastWrapper: ToastControllerWrapperService,
    private userDataService: UserDataService,
    private formatService: FormatService) {
    this.cntxt = this;
  }

  // The Validator and ControlValueAccessor interfaces are used to integrate the component with the FormsModule and ReactiveFormsModule

  // --> Validator interface

  onValidationChange: any = () => {};

  validate(control: AbstractControl): ValidationErrors {
    return null;
  }
  registerOnValidatorChange?(fn: () => void): void {
    this.onValidationChange = fn;
  }
  // --> End Validator interface

  // --> ControlValueAccessor interface

  onChange = (option) => { };
  onTouched = () => { };
  disabled = false;
  touched = false;

  writeValue(obj: AutocompleteOption): void {
    this.initialSearch = false;
    this.updateOption(obj);
  }
  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }
  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
    }
    this.touched = true;
  }
  // --> End ControlValueAccessor interface

  // Field debounce time
  autocompleteDebounceTime: number = environment.DEFAULT_AUTOCOMPLETE_DEBOUNCE_TIME;

  results$!: Observable<ReturnMessage<AutocompleteOption[]>>;
  private searchTerms = new Subject<string>();

  initDone: boolean = false;

  async ngOnInit() {
    this.autocompleteDebounceTime = await this.userDataService.getAutocompleteTime();

    this.selectionChange.subscribe((option: AutocompleteOption) => {

      this.setSearchTerm(option);
      
      // Removed existing options hidding options list.
      this.options = null;

      // Update the state of the component.
      this.markAsTouched();

      // Call the function event handler used by the validation interface
      this.onChange(this.selection);
      this.onValidationChange();
    });

    this.initDone = true;
  }

  determineSearchTerm(option: AutocompleteOption) : string
  {
    // Format (if necessary) the value of the input field.
    if (this.formatValue) 
    {
      if (typeof(this.formatValue) === 'function')
      {
        return option ? this.formatValue(option) : null;
      }
      else
      {
        return option ? this.formatValue.format(option) : null;  
      }
    }
    else 
    {
      return option ? option.description : null;
    }
  }

  setSearchTerm(option: AutocompleteOption) {
      // Set the value of the input field.
      this.searchTerm = this.determineSearchTerm(option);
  }

  async showAll() {

    if (!this.loader) {
      // console.log("No data loader service set for autocomplete component set");
      return;
    }

    this.initialSearch = false;

    if (this.allRequested && this.options != null) {
      // The user clicked the magnifying glass icon after all options were displayed so we close the list.
      this.options = null;

      // nothing was requested
      this.allRequested = !this.allRequested;
    } else {
      // The user clicked the magnifying glass icon when not all the options are displayed (or the list is closed) so we search for all available options and display the list.
      // show the progress bar below the field
      this.loading = true;

      // search all options
      this.searchTerms.next('');
    }
  }

  async search() {
    this.markAsTouched();

    if (!this.loader) {
      // console.log("No data loader service set for autocomplete component set");
      return;
    }

    if (this.skipSearch && !this.initialSearch) {
      // The user selected an option and the input field value changed but we don't want to trigger another search
      this.skipSearch = false;
      return;
    }

    if (this.initialSearch) {
      this.initialSearch = false;
    }

    // The user don't want to see all the available options.
    this.allRequested = false;

    if (this.searchTerm != '') {
      // show the progress bar
      this.loading = true;

      // search based on the search term
      this.searchTerms.next(this.searchTerm);

      // trigget the onChange event when the user type something in the input field to get the validations done again
      this.onChange(this.selection);
      // this.onValidationChange();

    } else {
      // When the search field is empty clear the selection by setting 'selection' to null
      this.options = null;

      // Also set the 'options' list to null to hide the results list.
      this.updateOption(null);

      this.onChange(this.selection);
      // this.onValidationChange();
    }
  }

  // Called when the user selects an option from the options list
  selectOption(option: AutocompleteOption) {
    this.updateOption(option);
  }

  // Called when the user click the X icon to clear the selected value
  clear() {
    this.markAsTouched();
    this.updateOption(null);
    this.closeOptionsList();
    this.onChange(this.selection);
    // this.onValidationChange();
  }

  closeOptionsList() {
    this.options = null;
    this.allRequested = false;
  }
}
